<script setup lang="ts">
  import type { QTableProps } from 'quasar'

  import { useQuery } from '@tanstack/vue-query'

  import {
    type DepartmentOut,
    listChildDepartmentsPassportIdmIdmPrivateListChildDepartmentsPost,
  } from '@/shared/api/intranet'
  import { UButton } from '@/shared/ui/components'
  import { ChevronRighIcon, FolderFullIcon, PlusIcon, UserIcon } from '@/shared/ui/svg'
  definePage({
    meta: {
      layout: 'admin',
    },
  })
  const router = useRouter()

  const tableConfig: QTableProps['columns'] = [
    {
      name: 'name',
      label: 'admin_departments_table_name',
      align: 'left',
      field: (row: DepartmentOut) => {
        return row.name
      },
    },
    {
      name: 'id',
      label: 'admin_departments_table_id',
      align: 'left',
      field: (row: DepartmentOut) => {
        return row.id
      },
    },
    {
      name: 'actions',
      label: 'admin_departments_table_actions',
      align: 'right',
      field: (row: DepartmentOut) => {
        return {
          id: row.id,
          usersCount: row.usersCount,
        }
      },
    },
  ]

  const gotoItem = (row: DepartmentOut) => {
    router.push(`/admin/departments/${row.id}`)
  }
  const { data: departments, isLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: () => {
      return listChildDepartmentsPassportIdmIdmPrivateListChildDepartmentsPost({
        body: {},
        throwOnError: true,
      })
    },
    select: (response) => response.data.departments,
  })
</script>

<template>
  <div class="flex h-full w-full flex-col gap-4">
    <div class="head">
      <div class="content flex items-center justify-between">
        <h2>{{ $t('admin_departments_title') }}</h2>
        <div class="flex gap-2 md:flex-col lg:flex-row">
          <UButton
            class="flex items-center gap-2 px-2"
            size="small"
            type="secondary"
            @click="$router.push('/admin/employees/new')"
          >
            <PlusIcon
              width="12"
              height="12"
            />
            {{ $t('admin_departments_add_employee') }}
          </UButton>
          <UButton
            class="flex items-center gap-2 px-2"
            size="small"
            type="secondary"
            @click="$router.push('/admin/departments/new')"
          >
            <PlusIcon
              width="12"
              height="12"
            />
            {{ $t('admin_departments_add_department') }}
          </UButton>
        </div>
      </div>
    </div>
    <QTable
      :columns="tableConfig"
      :rows="departments || []"
      hide-pagination
      :rows-per-page-options="[0]"
      :no-data-label="$t('admin_departments_no_data_available')"
      :loading="isLoading"
      @row-click="(_, row) => gotoItem(row)"
    >
      <template #loading>
        <QInnerLoading
          showing
          color="primary"
        />
      </template>
      <template #header-cell="headCellProps">
        <QTh :props="headCellProps">
          {{ $t(headCellProps.col.label) }}
        </QTh>
      </template>
      <template #body-cell-name="cellProps">
        <QTd>
          <div class="flex cursor-pointer items-center">
            <FolderFullIcon
              width="32"
              height="32"
              class="mr-2"
            />
            <div class="max-w-[200px] truncate">
              {{ cellProps.value }}
              <QTooltip :delay="500">
                <div class="max-w-[200px] text-[12px]">
                  {{ cellProps.value }}
                </div>
              </QTooltip>
            </div>
          </div>
        </QTd>
      </template>
      <template #header-cell-actions="headCellProps">
        <QTh :props="headCellProps">
          <span />
        </QTh>
      </template>
      <template #body-cell-actions="cellProps">
        <QTd>
          <div class="flex items-center justify-end">
            <div class="flex items-center">
              <UserIcon />
              {{ cellProps.value.usersCount }}
            </div>
            <ChevronRighIcon />
          </div>
        </QTd>
      </template>
    </QTable>
  </div>
</template>

<style lang="scss">
  .context-menu__wrapper {
    position: relative;
  }

  .context-menu {
    position: absolute;
    top: 13px;
    left: 100%;
    background: var(--white);
    border-radius: 12px;
    border: 1px solid var(--grey-light);
    background: var(--white-4);
    box-shadow: 0px 25px 50px -12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
  }

  .context-menu div {
    font-size: 12px;
    border-top: 1px solid var(--grey-light-2);
    display: flex;
    padding: 16px 12px;
    align-items: center;
    cursor: pointer;
    min-width: 150px;
  }
</style>
<style lang="scss" scoped>
  .q-table {
    @include font-montserrat-400(14px, 24px, var(--dark-black-2));
    max-height: 768px;
    min-height: 150px;
    :deep(th) {
      font-weight: 600;
    }
  }
  :deep(.q-table__bottom) {
    background-color: var(--grey);
  }
  :deep(thead) {
    background-color: var(--grey);
  }
</style>
